import React, { useEffect, useState } from 'react';
import { LeadRecord, OpportunityRecord, FunnelStep } from '../../types/throughput';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { format, parseISO } from 'date-fns';
import { Users, Briefcase } from 'lucide-react';
interface DrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  leads: LeadRecord[];
  opportunities: OpportunityRecord[];
  step: FunnelStep | 'All';
}
export function DrillDownModal({
  isOpen,
  onClose,
  title,
  leads,
  opportunities,
  step
}: DrillDownModalProps) {
  const [activeTab, setActiveTab] = useState<'leads' | 'opportunities'>('leads');
  // Reset to appropriate tab when modal opens
  useEffect(() => {
    if (isOpen) {
      const showOppsByDefault = step !== 'Created' && step !== 'All';
      setActiveTab(showOppsByDefault ? 'opportunities' : 'leads');
    }
  }, [isOpen, step]);
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return format(parseISO(dateStr), 'MMM d, yyyy');
  };
  const tabs = [{
    id: 'leads' as const,
    label: 'Leads',
    count: leads.length,
    icon: Users
  }, {
    id: 'opportunities' as const,
    label: 'Opportunities',
    count: opportunities.length,
    icon: Briefcase
  }];
  return <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl" data-id="element-6">
      <div className="flex flex-col h-[550px]" data-id="element-7">
        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-[#F5F5F5] rounded-lg mb-4 w-fit" data-id="element-8">
          {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return <button key={tab.id} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${isActive ? 'bg-white text-[#181818] shadow-sm' : 'text-[#706E6B] hover:text-[#181818]'}`} onClick={() => setActiveTab(tab.id)} data-id="element-9">
                <Icon className="h-4 w-4" data-id="element-10" />
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-[#0176D3]/10 text-[#0176D3]' : 'bg-[#E5E5E5] text-[#706E6B]'}`} data-id="element-11">
                  {tab.count}
                </span>
              </button>;
        })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto rounded-lg border border-[#E5E5E5]" data-id="element-12">
          {activeTab === 'leads' ? leads.length === 0 ? <div className="flex items-center justify-center h-full text-[#706E6B] text-sm" data-id="element-13">
                No leads to display
              </div> : <table className="w-full text-sm" data-id="element-14">
                <thead className="text-xs text-[#706E6B] uppercase bg-[#FAFAFA] sticky top-0" data-id="element-15">
                  <tr data-id="element-16">
                    <th className="px-4 py-3 font-semibold text-left" data-id="element-17">Name</th>
                    <th className="px-4 py-3 font-semibold text-left" data-id="element-18">
                      Source
                    </th>
                    <th className="px-4 py-3 font-semibold text-left" data-id="element-19">
                      Created
                    </th>
                    <th className="px-4 py-3 font-semibold text-left" data-id="element-20">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]" data-id="element-21">
                  {leads.map(lead => <tr key={lead.id} className="hover:bg-[#F5F9FD] transition-colors" data-id="element-22">
                      <td className="px-4 py-3 font-medium text-[#0176D3]" data-id="element-23">
                        {lead.name}
                      </td>
                      <td className="px-4 py-3 text-[#181818]" data-id="element-24">
                        {lead.source}
                      </td>
                      <td className="px-4 py-3 text-[#706E6B] tabular-nums" data-id="element-25">
                        {formatDate(lead.createdAt)}
                      </td>
                      <td className="px-4 py-3" data-id="element-26">
                        {lead.isConverted ? <Badge variant="success" data-id="element-27">Converted</Badge> : <Badge variant="neutral" data-id="element-28">Open</Badge>}
                      </td>
                    </tr>)}
                </tbody>
              </table> : opportunities.length === 0 ? <div className="flex items-center justify-center h-full text-[#706E6B] text-sm" data-id="element-29">
              No opportunities to display
            </div> : <table className="w-full text-sm" data-id="element-30">
              <thead className="text-xs text-[#706E6B] uppercase bg-[#FAFAFA] sticky top-0" data-id="element-31">
                <tr data-id="element-32">
                  <th className="px-4 py-3 font-semibold text-left" data-id="element-33">Name</th>
                  <th className="px-4 py-3 font-semibold text-left" data-id="element-34">Stage</th>
                  <th className="px-4 py-3 font-semibold text-right" data-id="element-35">Amount</th>
                  <th className="px-4 py-3 font-semibold text-left" data-id="element-36">
                    Converted
                  </th>
                  <th className="px-4 py-3 font-semibold text-left" data-id="element-37">Funded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]" data-id="element-38">
                {opportunities.map(opp => <tr key={opp.id} className="hover:bg-[#F5F9FD] transition-colors" data-id="element-39">
                    <td className="px-4 py-3 font-medium text-[#0176D3]" data-id="element-40">
                      {opp.name}
                    </td>
                    <td className="px-4 py-3" data-id="element-41">
                      <Badge variant={opp.stage === 'Funded' ? 'success' : 'brand'} data-id="element-42">
                        {opp.stage}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#181818]" data-id="element-43">
                      ${opp.amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-[#706E6B] tabular-nums" data-id="element-44">
                      {formatDate(opp.convertedDate)}
                    </td>
                    <td className="px-4 py-3 tabular-nums" data-id="element-45">
                      {opp.fundedDate ? <span className="text-[#04844B] font-medium" data-id="element-46">
                          {formatDate(opp.fundedDate)}
                        </span> : <span className="text-[#706E6B]" data-id="element-47">—</span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-[#E5E5E5] text-xs text-[#706E6B]" data-id="element-48">
          Showing {activeTab === 'leads' ? leads.length : opportunities.length}{' '}
          records
        </div>
      </div>
    </Modal>;
}